import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Caselle, Vuoto } from '../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, Modale, Paginazione, Riquadro, Scelta, Spunta, Tabella,
  totaleDi, type ConTotale, type TonoEtichetta,
} from '../componenti/base.tsx'
import { IconaPiu } from '../componenti/Icone.tsx'
import { vediComeUtente } from '../componenti/FasciaImpersonazione.tsx'
import { SceltaMultipla, useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { vociSedi } from '../servizi/sedi.ts'
import { codiceDaVoce, lingueDalCatalogo, opzioniConAltreVoci, type LinguaCatalogo } from '../servizi/lingue.ts'
import { scaricaCsv } from '../servizi/csv.ts'
import { AvvisoModulo, erroriDellApi, testoErrore, type Etichette } from '../componenti/errori-modulo.tsx'
import { descriviIncarico, DisattivaUtente, RiattivaUtente, type Precedente } from './UtentiDisattiva.tsx'
import { ImportaUtenti } from './ImportaUtenti.tsx'
import { CaselleDiGruppoUtente } from './UtentiCaselleGruppo.tsx'
import { DerogheEsterno, type CasellaPersonale } from './UtentiEsterni.tsx'
import { CampoCasellaCidis } from './CampoCasellaCidis.tsx'
import { AzzeraSecondoFattore } from './UtentiSecondoFattore.tsx'

/**
 * Amministrazione > Utenti: anagrafica degli operatori, cioe' degli utenti di sistema (D-25).
 *
 * Rapporto con CIDIS obbligatorio (interno o esterno), una o piu' sedi CIDIS con una principale,
 * data di nascita, comune di residenza o domicilio, paese di origine, lingue. L'elenco mostra
 * rapporto e sedi a chiunque legga gli utenti; i dati anagrafici compaiono solo nella finestra
 * di modifica, che apre chi gestisce le utenze e che l'API registra a ogni apertura.
 *
 * Dal 19/10/2026 (464): filtro per stato, «Disattiva» e «Riattiva» sulle righe (UtentiDisattiva.tsx)
 * e «Importa da foglio» (ImportaUtenti.tsx). Dal 30/10/2026 (483) la finestra di modifica ha la voce
 * «Caselle di gruppo» (UtentiCaselleGruppo.tsx). Dal 31/10/2026 (493) lo stato dice chi deve ancora
 * attivare il secondo fattore, e «Azzera il secondo fattore» (UtentiSecondoFattore.tsx) lo toglie a chi
 * ha perso il telefono.
 *
 * Dall'1/11/2026 (520): «Email personale» e «Crea la casella CIDIS» in «Aggiungi una persona» (l'indirizzo
 * proposto da nome e cognome, modificabile, controllato mentre si scrive), la colonna e il filtro
 * dell'email di recupero, l'email di recupero in sola lettura nella modifica, il «Nuovo invito» per chi
 * e' gia' entrato dopo una richiesta di recupero approvata.
 *
 * Dal 2/11/2026 (533): «È esterno?» in «Aggiungi una persona» (accesso e casella su
 * esterni.cidisonlus.org, rapporto esterno) e, nella modifica di un collaboratore esterno, le deroghe
 * (UtentiEsterni.tsx) e le sue caselle di servizio.
 */

const LIMITE = 50

/** Etichette dei campi dell'utenza, per gli errori che l'API restituisce con il codice del campo. */
const ETICHETTE_UTENTE: Etichette = {
  email: 'Email personale', nome: 'Nome', cognome: 'Cognome', tipo: 'Rapporto con CIDIS', ente_id: 'Ente',
  crea_casella: 'Crea la casella CIDIS', indirizzo_cidis: 'Indirizzo della casella CIDIS', esterno: 'È esterno?',
  scadenza_account: 'Scadenza dell’utenza', data_nascita: 'Data di nascita',
  comune_residenza_domicilio: 'Comune di residenza o domicilio', paese_origine: 'Paese di origine', lingue: 'Lingue',
  sedi: 'Sedi CIDIS', sede_principale: 'Sede principale', stato: 'Stato',
  motivo_disattivazione: 'Motivo della disattivazione', motivo_rapporto: 'Motivo della variazione del rapporto',
  adegua_incarichi: 'Adeguamento degli incarichi',
}

/** Errori dell'API senza dettagli, riconosciuti dal codice e portati sul campo a cui si riferiscono. */
const CODICI_CAMPO_UTENTE: Record<string, string> = {
  COMUNE: 'comune_residenza_domicilio', DATA_NASCITA: 'data_nascita', EMAIL_ESISTENTE: 'email', LINGUA: 'lingue',
  EMAIL_DI_CIDIS: 'email', EMAIL_NON_VALIDA: 'email', INDIRIZZO_IN_USO: 'indirizzo_cidis',
  INDIRIZZO_NON_VALIDO: 'indirizzo_cidis', CASELLA_GIA_PRESENTE: 'indirizzo_cidis', RAPPORTO_ESTERNO: 'tipo',
  ESTERNO_CON_CASELLA: 'esterno',
  PAESE: 'paese_origine', SCADENZA_ACCOUNT: 'scadenza_account', SEDE_INESISTENTE: 'sedi', SEDE_NON_ATTIVA: 'sedi',
  SEDE_OBBLIGATORIA: 'sedi', SEDE_PRINCIPALE_MANCANTE: 'sede_principale', MOTIVO_RICHIESTO: 'motivo_rapporto',
}

/**
 * Messaggio di un errore fuori dai moduli (elenco, apertura della scheda). I campi si nominano con
 * l'etichetta a video: prima usciva il codice tecnico, «Dati non validi: comune_residenza_domicilio.».
 */
function messaggioErrore(e: unknown): string {
  if (!(e instanceof ErroreApi)) return 'Non è andata a buon fine: controlla la connessione e riprova.'
  return e.dettagli?.length
    ? `${e.message}. ${e.dettagli.map((d) => testoErrore(d.campo, d.messaggio, ETICHETTE_UTENTE)).join(' ')}`
    : e.message
}

/** Data dell'API (AAAA-MM-GG, anche con l'ora in coda) in forma italiana. */
const giorno = (v: string | null | undefined) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—')
const traGiorni = (giorni: number) => new Date(Date.now() + giorni * 86_400_000).toISOString().slice(0, 10)
/** Il giorno prima, AAAA-MM-GG: l'ultimo della grazia del secondo fattore (493). */
const giornoPrima = (g: string) => new Date(Date.parse(`${g}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10)

/** Durata massima dell'utenza di un esterno: la stessa regola dell'API (Q-H13, dodici mesi). */
const GIORNI_MASSIMI_ESTERNO = 365

const RAPPORTI: Record<string, { etichetta: string; tono: TonoEtichetta }> = {
  interno: { etichetta: 'Interno', tono: 'neutro' },
  esterno: { etichetta: 'Esterno', tono: 'informativo' },
  fornitore: { etichetta: 'Fornitore (tecnico)', tono: 'neutro' },
  storico: { etichetta: 'Autore storico (tecnico)', tono: 'neutro' },
}
/** Fornitore e autori storici: valori tecnici, fuori dalla scelta dell'amministratore. */
const tecnico = (tipo: string) => tipo === 'fornitore' || tipo === 'storico'

type SedeUtente = { id: string; codice: string; nome: string; principale: boolean; attiva?: boolean }
type Incarico = {
  id: string; ruolo: string; ambito_tipo: string; ambito_id: string | null; valida_a: string | null
  /** Nome della sede, dell'area, del progetto, dell'ente o della tipologia: serve al foglio scaricato. */
  ambito_nome?: string | null
}
/** Esito dell'invito: com'e' andata l'email, e le due meta' da consegnare. */
export type InvitoConsegnato = {
  codice_da_consegnare_a_voce: string; collegamento: string; scade_il: string
  email_inviata: boolean; motivo_email: string | null
  /** Dove e' partita l'email (520): all'email con cui si entra o all'email personale. */
  inviato_a?: 'accesso' | 'recupero' | null
}
export type PersonaInvitata = { id: string; nome: string; cognome: string }
/** Riattivazione (464): gli incarichi di prima, da riassegnare a mano. */
type Riattivazione = { precedenti: Precedente[] }
/**
 * Percorso guidato per aggiungere una persona (indicazioni del 17/10/2026): i dati, poi che
 * cosa potra' fare, poi come consegnarle l'invito. Chi non assegna incarichi salta il secondo.
 * La riattivazione (464) percorre gli ultimi due passi. «Dai l'accesso» a un autore del vecchio
 * archivio (468) li percorre tutti e tre, con i dati gia' compilati e la sua utenza: `storico`.
 */
type Percorso =
  | { passo: 'dati'; storico?: PersonaInvitata }
  | { passo: 'incarichi'; utente: PersonaInvitata; invito: InvitoConsegnato; riattivazione?: Riattivazione; storico?: boolean }
  | { passo: 'consegna'; utente: PersonaInvitata; invito: InvitoConsegnato; riattivazione?: Riattivazione; storico?: boolean
      /** Nuovo invito dopo una richiesta di recupero approvata (520): sostituisce la password. */
      recupero?: boolean }

/** Risposta di «Aggiungi una persona»: `autore_storico` se l'email era di un autore del vecchio archivio (468). */
type EsitoInvito = { id: string; autore_storico?: boolean } & InvitoConsegnato

type Utente = ConTotale & {
  id: string; email: string; nome: string; cognome: string; tipo: string; stato: string
  mfa_attiva: boolean; scadenza_account: string | null; ultimo_accesso: string | null
  disattivato_il: string | null
  /** Da quando i suoi incarichi esigono il secondo fattore (493), AAAA-MM-GG; null senza obbligo. */
  mfa_obbligatoria_dal?: string | null
  /** Autore del vecchio archivio eliminato dall'elenco (469). */
  eliminato_il?: string | null
  /** Email di recupero (520): verificata, da verificare o mancante; mai l'indirizzo. */
  email_recupero?: 'verificata' | 'da_verificare' | 'mancante'
  /** Richiesta di recupero approvata senza email di recupero verificata: aspetta un «Nuovo invito». */
  recupero_da_invitare?: boolean
  incarichi: Incarico[]; sedi: SedeUtente[]
}

/** Stato dell'utenza a parole (464): l'etichetta grezza «invitato» non diceva che cosa manca. */
/** Email di recupero a parole (520). */
const RECUPERO: Record<'verificata' | 'da_verificare' | 'mancante', { etichetta: string; tono: TonoEtichetta }> = {
  verificata: { etichetta: 'Verificata', tono: 'positivo' },
  da_verificare: { etichetta: 'Da verificare', tono: 'neutro' },
  mancante: { etichetta: 'Manca', tono: 'attenzione' },
}

const STATI: Record<string, { etichetta: string; tono: TonoEtichetta }> = {
  attivo: { etichetta: 'Attivo', tono: 'positivo' },
  invitato: { etichetta: 'Invitato, non ancora entrato', tono: 'attenzione' },
  disattivato: { etichetta: 'Disattivato', tono: 'critico' },
  sospeso: { etichetta: 'Sospeso', tono: 'critico' },
}
type SchedaUtente = {
  id: string; email: string; nome: string; cognome: string; tipo: string; stato: string
  scadenza_account: string | null; data_nascita: string | null
  comune_residenza_domicilio: string | null; comune_nome: string | null; comune_provincia: string | null
  paese_origine: string | null; paese_nome: string | null
  lingue: { codice: string; nome: string }[]
  sedi: SedeUtente[]
  incarichi: Incarico[]
  variazioni_rapporto: {
    tipo_precedente: string; tipo_nuovo: string; motivazione: string
    incarichi_adeguati: number; variata_il: string; variata_da: string
  }[]
  /** Email di recupero (520): la vede chi gestisce gli utenti, la cambia solo la persona. */
  email_recupero?: { indirizzo: string; verificata_il: string | null } | null
  email_recupero_nuova_dal?: string | null
  /** Collaboratore esterno (533): entra con un indirizzo @esterni.cidisonlus.org. */
  accesso_esterni?: boolean
  casella_personale?: CasellaPersonale | null
  variazioni_stato?: {
    azione: 'disattivazione' | 'riattivazione' | 'accesso_storico' | 'eliminazione_elenco' | 'ripristino_elenco'
    motivazione: string | null; incarichi_revocati: number
    variata_il: string; variata_da: string
  }[]
}
type IncaricoDaAdeguare = Incarico & { azione: 'revoca' | 'scadenza'; nuova_scadenza: string | null }
type Lingua = LinguaCatalogo
type Comune = { codice_istat: string; nome: string; provincia_sigla: string; soppresso_il?: string | null }
type VoceSede = { id: string; nome: string }

/** Stato del modulo di invito e di modifica: stringa vuota per un campo non compilato. */
type Modulo = {
  nome: string; cognome: string; email: string; tipo: string; scadenza_account: string
  data_nascita: string; comune: string; comuneTesto: string; paese_origine: string
  lingue: string[]; sedi: string[]; sede_principale: string
  /**
   * Casella CIDIS (520), solo per chi si aggiunge: la spunta, toccata o no da chi aggiunge (se no
   * segue il rapporto), l'indirizzo scritto (vuoto: la proposta) e l'esito dell'ultimo controllo.
   */
  crea_casella: boolean; casella_toccata: boolean; indirizzo_cidis: string; indirizzo_libero: boolean | null
  /**
   * «È esterno?» (533), solo per chi si aggiunge: accesso e casella su esterni.cidisonlus.org, rapporto
   * esterno. `indirizzo_cidis` e' allora l'indirizzo @esterni.
   */
  esterno: boolean
}

const MODULO_VUOTO: Modulo = {
  nome: '', cognome: '', email: '', tipo: '', scadenza_account: '', data_nascita: '',
  comune: '', comuneTesto: '', paese_origine: '', lingue: [], sedi: [], sede_principale: '',
  crea_casella: false, casella_toccata: false, indirizzo_cidis: '', indirizzo_libero: null, esterno: false,
}

/** Cosa manca perche' il modulo si possa inviare. Le stesse regole le applica l'API. */
function mancanze(m: Modulo, nuovo: boolean, collaboratoreRegistrato = false): string[] {
  // Collaboratore esterno (533): la scadenza dell'utenza e' facoltativa (Davide, 2/11/2026).
  const collaboratore = nuovo ? m.esterno : collaboratoreRegistrato
  const elenco: string[] = []
  if (!m.nome.trim()) elenco.push('nome')
  if (!m.cognome.trim()) elenco.push('cognome')
  if (nuovo && !m.email.trim()) elenco.push('email personale')
  if (nuovo && m.crea_casella && m.indirizzo_libero !== true) elenco.push('indirizzo della casella CIDIS libero')
  if (nuovo && m.esterno && m.indirizzo_libero !== true) elenco.push('indirizzo @esterni libero')
  if (!m.tipo) elenco.push('rapporto con CIDIS')
  if (m.tipo === 'esterno' && !m.scadenza_account && !collaboratore) elenco.push('scadenza dell’utenza')
  if (!tecnico(m.tipo) && m.sedi.length === 0) elenco.push('almeno una sede CIDIS')
  if (m.sedi.length > 1 && !m.sede_principale) elenco.push('sede principale')
  if (m.comuneTesto.trim() && !m.comune) elenco.push('comune scelto fra i suggerimenti')
  return elenco
}

/** Rapporto e scadenza gia' registrati: assenti nell'invito. */
type Registrato = { tipo: string; scadenza_account: string | null }

/**
 * Scadenza dell'utenza da inviare. Un esterno la indica sempre. Da esterno a interno si toglie,
 * come fa l'API: altrimenti resterebbe invisibile e alla data chiuderebbe fuori l'utente. Chi non
 * e' esterno ma ha gia' una scadenza la vede nel modulo e la invia com'e', vuota per toglierla.
 * Negli altri casi non viaggia e resta quella registrata.
 */
function scadenzaDaInviare(m: Modulo, registrato?: Registrato): string | null | undefined {
  if (m.tipo === 'esterno') return m.scadenza_account || null
  if (!registrato) return undefined
  if (registrato.tipo === 'esterno') return null
  return registrato.scadenza_account ? m.scadenza_account || null : undefined
}

/** Campi comuni a invito e modifica. Le chiavi con valore undefined non viaggiano. */
function corpoAnagrafico(m: Modulo, registrato?: Registrato) {
  return {
    nome: m.nome.trim(),
    cognome: m.cognome.trim(),
    scadenza_account: scadenzaDaInviare(m, registrato),
    data_nascita: m.data_nascita || null,
    comune_residenza_domicilio: m.comune || null,
    paese_origine: m.paese_origine || null,
    lingue: m.lingue,
    sedi: m.sedi,
    sede_principale: m.sede_principale || null,
  }
}

// --- campi --------------------------------------------------------------------------------------

/**
 * Comune scelto fra i suggerimenti di un <datalist>, come nella registrazione dell'intervento:
 * i componenti di base non prevedono la ricerca, e il campo ripete il markup di `Campo`.
 */
function CampoComune({ testo, codice, cambia, errore }: {
  testo: string
  codice: string
  cambia: (testo: string, codice: string) => void
  errore?: string | null
}) {
  const id = useId()
  const [comuni, setComuni] = useState<Comune[]>([])

  useEffect(() => {
    const termine = testo.trim()
    if (termine.length < 2 || codice) return
    const t = setTimeout(() => {
      api.get<Comune[]>(`/riferimenti/comuni?cerca=${encodeURIComponent(termine)}&limite=20`)
        // I comuni soppressi non si scelgono: l'API li rifiuterebbe.
        .then((r) => setComuni(r.filter((c) => !c.soppresso_il)))
        .catch(() => {})
    }, 300)
    return () => clearTimeout(t)
  }, [testo, codice])

  const messaggio = errore ?? (testo.trim() !== '' && !codice ? 'Scegliere il comune fra i suggerimenti.' : null)
  const errato = !!messaggio
  return (
    <div className={`campo${errato ? ' campo-errato' : ''}`}>
      <label htmlFor={id}>Comune di residenza o domicilio</label>
      <input id={id} list={`${id}-elenco`} value={testo} autoComplete="off" placeholder="Nome del comune"
             aria-invalid={errato ? true : undefined} aria-describedby={`${id}-nota`}
             onChange={(e) => {
               const valore = e.target.value
               const scelto = comuni.find((c) => `${c.nome} (${c.provincia_sigla})` === valore)
               cambia(valore, scelto?.codice_istat ?? '')
             }} />
      <datalist id={`${id}-elenco`}>
        {comuni.map((c) => <option key={c.codice_istat} value={`${c.nome} (${c.provincia_sigla})`} />)}
      </datalist>
      {errato
        ? <p className="campo-errore" id={`${id}-nota`}>{messaggio}</p>
        : <p className="campo-aiuto" id={`${id}-nota`}>Residenza o, se diverso, domicilio.</p>}
    </div>
  )
}

/**
 * Lingue: una tendina e un comando per aggiungere. La lingua non si aggiunge al cambio della
 * tendina, perche' con la tastiera alcuni browser la cambiano a ogni freccia.
 */
function CampoLingue({ catalogo, scelte, cambia, errore }: {
  catalogo: Lingua[]
  scelte: string[]
  cambia: (lingue: string[]) => void
  errore?: string | null
}) {
  const [daAggiungere, setDaAggiungere] = useState('')
  const nome = (codice: string) => catalogo.find((l) => l.codice_iso === codice)?.nome ?? codice
  return (
    <fieldset className="gruppo-caselle">
      <legend>Lingue</legend>
      <div className="riga campo-in-linea">
        <Scelta etichetta="Lingua da aggiungere" vuota="— seleziona —" valore={daAggiungere}
                cambia={setDaAggiungere} errore={errore}
                opzioni={opzioniConAltreVoci(lingueDalCatalogo(catalogo), scelte)} />
        <Bottone disabled={!daAggiungere}
                 onClick={() => { cambia([...scelte, codiceDaVoce(daAggiungere)]); setDaAggiungere('') }}>
          Aggiungi
        </Bottone>
      </div>
      {scelte.length === 0
        ? <p className="piccolo tenue">Nessuna lingua indicata.</p>
        : (
          <ul className="operatori-lingue" aria-label="Lingue indicate">
            {scelte.map((c) => (
              <li key={c} className="operatori-lingua">
                <span>{nome(c)}</span>
                <Bottone variante="discreto" dimensione="piccolo" aria-label={`Togli ${nome(c)}`}
                         onClick={() => cambia(scelte.filter((x) => x !== c))}>
                  Togli
                </Bottone>
              </li>
            ))}
          </ul>
        )}
    </fieldset>
  )
}

function CampoSedi({ catalogo, scelte, principale, obbligatorie, cambia, errori = {} }: {
  catalogo: VoceSede[]
  scelte: string[]
  principale: string
  obbligatorie: boolean
  cambia: (sedi: string[], principale: string) => void
  errori?: Record<string, string>
}) {
  // Con una sola sede la principale e' quella; togliendo la principale si torna a sceglierla.
  const aggiorna = (nuove: string[]) =>
    cambia(nuove, nuove.includes(principale) ? principale : nuove.length === 1 ? nuove[0] : '')
  return (
    <fieldset className={`gruppo-caselle${errori.sedi ? ' campo-errato' : ''}`}>
      <legend>
        Sedi CIDIS{obbligatorie && <span className="campo-obbligatorio" aria-hidden="true"> *</span>}
      </legend>
      {catalogo.length === 0
        ? <p className="piccolo tenue">Nessuna sede CIDIS attiva.</p>
        : <Caselle voci={catalogo.map((s) => ({ codice: s.id, nome: s.nome }))} scelte={scelte} cambia={aggiorna} />}
      {errori.sedi && <p className="campo-errore" role="alert">{errori.sedi}</p>}
      {scelte.length > 1 && (
        <div className="operatori-sede-principale">
          <Scelta etichetta="Sede principale" obbligatorio vuota="— seleziona —" valore={principale}
                  cambia={(v) => cambia(scelte, v)} errore={errori.sede_principale}
                  opzioni={catalogo.filter((s) => scelte.includes(s.id))
                    .map((s) => ({ valore: s.id, etichetta: s.nome }))} />
        </div>
      )}
      {scelte.length === 1 && <p className="piccolo tenue">Con una sola sede, quella è la sede principale.</p>}
    </fieldset>
  )
}

function CampiAnagrafici({
  modulo, cambia, nuovo, catalogoSedi, catalogoLingue, rapportoRegistrato, scadenzaFacoltativa, paeseRegistrato,
  errori = {}, utenteCasella, collaboratore = false,
}: {
  /** «Dai l'accesso» (468): l'utenza che c'e' gia', il cui indirizzo non conta come occupato. */
  utenteCasella?: string
  /** Errori dell'ultimo invio, per codice del campo nell'API. */
  errori?: Record<string, string>
  modulo: Modulo
  cambia: (parziale: Partial<Modulo>) => void
  nuovo: boolean
  catalogoSedi: VoceSede[]
  catalogoLingue: Lingua[]
  /** Rapporto registrato prima della modifica; assente nell'invito. */
  rapportoRegistrato?: string
  /** Chi non e' esterno ha gia' una scadenza dell'utenza: il campo resta visibile per toglierla. */
  scadenzaFacoltativa?: boolean
  /** Paese gia' registrato, perche' resti visibile anche se non piu' in catalogo. */
  paeseRegistrato?: { codice: string; nome: string }
  /** Collaboratore esterno gia' registrato (533): la scadenza dell'utenza e' facoltativa. */
  collaboratore?: boolean
}) {
  const collaboratoreEsterno = nuovo ? modulo.esterno : collaboratore
  const { paesi } = useSessione()
  const bloccato = rapportoRegistrato !== undefined && tecnico(rapportoRegistrato)
  const opzioniRapporto = rapportoRegistrato !== undefined && bloccato
    ? [{ valore: rapportoRegistrato, etichetta: RAPPORTI[rapportoRegistrato]?.etichetta ?? rapportoRegistrato }]
    : [{ valore: 'interno', etichetta: 'Interno' }, { valore: 'esterno', etichetta: 'Esterno' }]
  const opzioniPaese = paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome }))
  if (paeseRegistrato && !opzioniPaese.some((o) => o.valore === paeseRegistrato.codice)) {
    opzioniPaese.push({ valore: paeseRegistrato.codice, etichetta: paeseRegistrato.nome })
  }

  return (
    <>
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio maxLength={100} valore={modulo.nome}
               cambia={(v) => cambia({ nome: v })} errore={errori.nome} />
        <Campo etichetta="Cognome" obbligatorio maxLength={100} valore={modulo.cognome}
               cambia={(v) => cambia({ cognome: v })} errore={errori.cognome} />
        {nuovo && (
          <Campo etichetta="Email personale" tipo="email" obbligatorio autoComplete="off" valore={modulo.email}
                 cambia={(v) => cambia({ email: v })} errore={errori.email}
                 aiuto="Dove arrivano l’invito e poi il recupero della password." />
        )}
        {/* La casella CIDIS (520) si propone ai rapporti interni, finche' chi aggiunge non la sceglie lui. */}
        <Scelta etichetta="Rapporto con CIDIS" obbligatorio vuota={nuovo ? '— seleziona —' : undefined}
                valore={modulo.tipo} disabilitato={bloccato || (nuovo && modulo.esterno)} opzioni={opzioniRapporto}
                errore={errori.tipo}
                cambia={(v) => cambia({ tipo: v, ...(nuovo && !modulo.casella_toccata ? { crea_casella: v === 'interno' } : {}) })}
                aiuto={bloccato
                  ? 'Valore tecnico: non si modifica.'
                  : collaboratoreEsterno
                    ? 'Collaboratore esterno: la scadenza dell’utenza è facoltativa.'
                    : 'L’utenza di un esterno scade al massimo dopo dodici mesi.'} />
        {modulo.tipo === 'esterno'
          ? (
            <Campo etichetta="Scadenza dell’utenza" tipo="date" obbligatorio={!collaboratoreEsterno}
                   min={traGiorni(0)} max={traGiorni(GIORNI_MASSIMI_ESTERNO)} errore={errori.scadenza_account}
                   valore={modulo.scadenza_account} cambia={(v) => cambia({ scadenza_account: v })}
                   aiuto={collaboratoreEsterno
                     ? 'Facoltativa per i collaboratori esterni: dopo questa data non entra più. Al massimo dodici mesi.'
                     : undefined} />
          )
          : scadenzaFacoltativa && (
            <Campo etichetta="Scadenza dell’utenza" tipo="date" errore={errori.scadenza_account}
                   valore={modulo.scadenza_account} cambia={(v) => cambia({ scadenza_account: v })}
                   aiuto="Registrata in precedenza: dopo questa data l’utente non accede. Svuotare il campo per toglierla." />
          )}
        <Campo etichetta="Data di nascita" tipo="date" min="1900-01-01" max={traGiorni(0)}
               valore={modulo.data_nascita} cambia={(v) => cambia({ data_nascita: v })} errore={errori.data_nascita} />
        <CampoComune testo={modulo.comuneTesto} codice={modulo.comune} errore={errori.comune_residenza_domicilio}
                     cambia={(testo, codice) => cambia({ comuneTesto: testo, comune: codice })} />
        <Scelta etichetta="Paese di origine" vuota="— non indicato —" valore={modulo.paese_origine}
                opzioni={opzioniPaese} cambia={(v) => cambia({ paese_origine: v })} errore={errori.paese_origine} />
      </div>
      {nuovo && (
        <div className="operatori-sezione">
          {/* «È esterno?» (533): collaboratori senza la casella cidisonlus.org. Il rapporto diventa esterno,
              l'accesso e la casella nascono su esterni.cidisonlus.org, chiusa verso fuori. */}
          <Spunta etichetta="È esterno?" valore={modulo.esterno}
                  cambia={(v) => cambia({
                    esterno: v, indirizzo_cidis: '', indirizzo_libero: null,
                    ...(v ? { tipo: 'esterno', crea_casella: false, casella_toccata: true } : {}),
                  })}
                  aiuto={modulo.esterno
                    ? 'Collaboratore esterno: entra con un indirizzo …@esterni.cidisonlus.org e trova la sua casella '
                      + 'in «Posta», che scrive e riceve solo dentro CIDIS. Le deroghe (ricezione e invio fuori, '
                      + 'casella cidisonlus.org) si danno dopo, dalla sua scheda. L’invito parte all’email personale.'
                    : 'Per i collaboratori esterni che non devono avere la casella cidisonlus.org.'} />
          {modulo.esterno && (
            <div className="griglia-modulo">
              <CampoCasellaCidis nome={modulo.nome} cognome={modulo.cognome} valore={modulo.indirizzo_cidis} esterno
                                 cambia={cambia} utenteId={utenteCasella} errore={errori.indirizzo_cidis} />
            </div>
          )}
          {!modulo.esterno && (
            <Spunta etichetta="Crea la casella CIDIS" valore={modulo.crea_casella}
                    cambia={(v) => cambia({ crea_casella: v, casella_toccata: true })}
                    aiuto={modulo.crea_casella
                      ? 'La persona entra con l’indirizzo CIDIS e trova la sua casella in «Posta». L’invito parte all’email personale.'
                      : 'Senza casella la persona entra con l’email personale.'} />
          )}
          {modulo.crea_casella && !modulo.esterno && (
            <div className="griglia-modulo">
              <CampoCasellaCidis nome={modulo.nome} cognome={modulo.cognome} valore={modulo.indirizzo_cidis}
                                 cambia={cambia} utenteId={utenteCasella} errore={errori.indirizzo_cidis} />
            </div>
          )}
        </div>
      )}
      <CampoSedi catalogo={catalogoSedi} scelte={modulo.sedi} principale={modulo.sede_principale}
                 obbligatorie={!tecnico(modulo.tipo)} errori={errori}
                 cambia={(sedi, principale) => cambia({ sedi, sede_principale: principale })} />
      <CampoLingue catalogo={catalogoLingue} scelte={modulo.lingue} cambia={(lingue) => cambia({ lingue })}
                   errore={errori.lingue} />
    </>
  )
}

// --- elenco --------------------------------------------------------------------------------------

export function Utenti() {
  const { puo, sedi: sediAttive, io } = useSessione()
  const gestisce = puo('utente.gestisci')
  // Assegnare incarichi e' un permesso a se' (`ruolo.assegna`): chi amministra le anagrafiche
  // delle utenze non per questo apre gli accessi ai dati.
  const assegna = puo('ruolo.assegna')
  const [utenti, setUtenti] = useState<Utente[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [salta, setSalta] = useState(0)
  const [cerca, setCerca] = useState('')
  const [termine, setTermine] = useState('')
  const [rapporto, setRapporto] = useState('')
  const [sede, setSede] = useState('')
  // Per difetto solo chi lavora in CIDIS: storici e disattivati si vedono scegliendo lo stato.
  const [stato, setStato] = useState('in_uso')
  // Email di recupero (520): chi non l'ha verificata, o chi si'.
  const [recupero, setRecupero] = useState('')
  const [percorso, setPercorso] = useState<Percorso | null>(null)
  const [daDisattivare, setDaDisattivare] = useState<string | null>(null)
  // «Vedi come» (524): l'utente di cui si sta aprendo l'impersonazione.
  const [vediCome, setVediCome] = useState<string | null>(null)
  const [daRiattivare, setDaRiattivare] = useState<PersonaInvitata | null>(null)
  const [daEliminare, setDaEliminare] = useState<Utente | null>(null)
  const [daAzzerare, setDaAzzerare] = useState<Utente | null>(null)
  const [ripristino, setRipristino] = useState<string | null>(null)
  const [importa, setImporta] = useState(false)
  const [scaricando, setScaricando] = useState(false)
  // Nomi dei ruoli per la colonna degli incarichi: la sigla da sola non dice niente a chi legge.
  const [nomiRuoli, setNomiRuoli] = useState<Record<string, string>>({})
  const [rinnovo, setRinnovo] = useState<string | null>(null)
  const [nuovo, setNuovo] = useState<Modulo>(MODULO_VUOTO)
  const [inCorso, setInCorso] = useState(false)
  const [lingue, setLingue] = useState<Lingua[]>([])
  const [daModificare, setDaModificare] = useState<string | null>(null)
  const [incarichiDi, setIncarichiDi] = useState<Utente | null>(null)
  // Esito dell'invito accanto ai comandi del riquadro, con gli errori sui campi e il fuoco sul primo.
  const [erroreInvito, setErroreInvito] = useState<string | null>(null)
  const [erroriInvito, setErroriInvito] = useState<Record<string, string>>({})
  const [tentativoInvito, setTentativoInvito] = useState(0)
  const radiceInvito = useRef<HTMLDivElement>(null)
  useFuocoSulPrimoErrore(radiceInvito, tentativoInvito)

  // La ricerca parte quando si smette di scrivere, e riporta la finestra all'inizio.
  useEffect(() => {
    const t = setTimeout(() => {
      setTermine((prima) => {
        const dopo = cerca.trim()
        if (dopo !== prima) setSalta(0)
        return dopo
      })
    }, 300)
    return () => clearTimeout(t)
  }, [cerca])

  const carica = useCallback(() => {
    const parametri = new URLSearchParams({ limite: String(LIMITE), salta: String(salta) })
    if (termine) parametri.set('cerca', termine)
    if (rapporto) parametri.set('tipo', rapporto)
    if (sede) parametri.set('sede', sede)
    if (stato) parametri.set('stato', stato)
    if (recupero) parametri.set('recupero', recupero)
    api.get<Utente[]>(`/amministrazione/utenti?${parametri.toString()}`)
      .then(setUtenti)
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [salta, termine, rapporto, sede, stato, recupero])
  useEffect(carica, [carica])

  useEffect(() => {
    if (!gestisce) return
    api.get<Lingua[]>('/riferimenti/lingue').then(setLingue).catch(() => {})
  }, [gestisce])

  useEffect(() => {
    api.get<{ ruoli: { codice: string; nome: string }[] }>('/amministrazione/ruoli')
      .then((r) => setNomiRuoli(Object.fromEntries(r.ruoli.map((x) => [x.codice, x.nome]))))
      .catch(() => {})
  }, [])

  async function invita() {
    setErrore(null); setMessaggio(null); setInCorso(true)
    setErroreInvito(null); setErroriInvito({})
    const daStorico = percorso?.passo === 'dati' ? percorso.storico : undefined
    try {
      const corpo = {
        ...corpoAnagrafico(nuovo), email: nuovo.email.trim(), tipo: nuovo.esterno ? 'esterno' : nuovo.tipo,
        crea_casella: nuovo.crea_casella && !nuovo.esterno, esterno: nuovo.esterno,
        indirizzo_cidis: nuovo.crea_casella || nuovo.esterno ? nuovo.indirizzo_cidis || null : null,
      }
      const esito = daStorico
        ? await api.post<EsitoInvito>(`/amministrazione/utenti/${daStorico.id}/accesso`, corpo)
        : await api.post<EsitoInvito>('/amministrazione/utenti', corpo)
      const utente = { id: esito.id, nome: nuovo.nome.trim(), cognome: nuovo.cognome.trim() }
      const storico = !!daStorico || !!esito.autore_storico
      setNuovo(MODULO_VUOTO)
      carica()
      setPercorso(assegna
        ? { passo: 'incarichi', utente, invito: esito, storico }
        : { passo: 'consegna', utente, invito: esito, storico })
    } catch (e) {
      setErroreInvito(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      setErroriInvito(erroriDellApi(e, CODICI_CAMPO_UTENTE))
      setTentativoInvito((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  const mancaNuovo = mancanze(nuovo, true)

  /**
   * «Dai l'accesso» a un autore del vecchio archivio (468): il modulo di «Aggiungi una persona» con
   * i dati che la migrazione ha ricavato dall'indirizzo. Il cognome «storico» e l'indirizzo
   * segnaposto di chi nel vecchio gestionale non aveva un account non sono dati veri: restano vuoti.
   */
  function daiAccesso(u: Utente) {
    setErrore(null); setMessaggio(null); setErroreInvito(null); setErroriInvito({})
    setNuovo({
      ...MODULO_VUOTO, nome: u.nome, cognome: u.cognome === 'storico' ? '' : u.cognome,
      email: u.email.endsWith('@storico.cidis.invalid') ? '' : u.email,
    })
    setPercorso({ passo: 'dati', storico: { id: u.id, nome: u.nome, cognome: u.cognome } })
  }

  /** Un autore eliminato dall'elenco torna fra gli storici: da li' gli si puo' dare l'accesso (469). */
  async function ripristina(u: Utente) {
    setErrore(null); setMessaggio(null); setRipristino(u.id)
    try {
      await api.post(`/amministrazione/utenti/${u.id}/ripristina-nell-elenco`, {})
      setMessaggio(`${u.nome} ${u.cognome} è di nuovo nell’elenco, fra i «Disattivati e storici».`)
      carica()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setRipristino(null)
    }
  }

  /** Chiude il primo passo; i dati di un autore storico non restano nel modulo di «Aggiungi una persona». */
  function chiudiDati() {
    if (percorso?.passo === 'dati' && percorso.storico) setNuovo(MODULO_VUOTO)
    setPercorso(null)
  }

  /** Invito nuovo per chi non e' ancora entrato: il collegamento precedente smette di valere. */
  async function rinnovaInvito(u: Utente) {
    setErrore(null); setMessaggio(null); setRinnovo(u.id)
    try {
      const invito = await api.post<InvitoConsegnato>(`/amministrazione/utenti/${u.id}/invito`, {})
      setPercorso({ passo: 'consegna', utente: { id: u.id, nome: u.nome, cognome: u.cognome }, invito,
        recupero: u.stato !== 'invitato' })
      if (u.stato !== 'invitato') carica()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setRinnovo(null)
    }
  }

  /**
   * «Scarica l'elenco» (Osservazioni 5 del 28/10/2026, come nella pagina Mediatori): le persone che
   * corrispondono ai filtri, tutte e non solo la pagina visibile, in un foglio CSV per Excel.
   */
  async function scaricaElenco() {
    setErrore(null); setMessaggio(null); setScaricando(true)
    try {
      const tutti: Utente[] = []
      for (let da = 0; ; da += 500) {
        const parametri = new URLSearchParams({ limite: '500', salta: String(da), scopo: 'scarico' })
        if (termine) parametri.set('cerca', termine)
        if (rapporto) parametri.set('tipo', rapporto)
        if (sede) parametri.set('sede', sede)
        if (stato) parametri.set('stato', stato)
        if (recupero) parametri.set('recupero', recupero)
        const pagina = await api.get<Utente[]>(`/amministrazione/utenti?${parametri.toString()}`)
        tutti.push(...pagina)
        if (pagina.length < 500) break
      }
      const incarico = (i: Incarico) => [
        nomiRuoli[i.ruolo] ?? i.ruolo,
        i.ambito_tipo === 'organizzazione' ? 'tutta CIDIS' : i.ambito_nome ?? AMBITI_ETICHETTE[i.ambito_tipo] ?? i.ambito_tipo,
        ...(i.valida_a ? [`fino al ${giorno(i.valida_a)}`] : []),
      ].join(' · ')
      scaricaCsv(`utenti-${traGiorni(0)}.csv`,
        ['Cognome', 'Nome', 'Email', 'Rapporto con CIDIS', 'Scadenza dell’utenza', 'Sede principale', 'Altre sedi',
          'Incarichi', 'Stato', 'Secondo fattore', 'Email di recupero', 'Ultimo accesso'],
        tutti.map((u) => [u.cognome, u.nome, u.email, RAPPORTI[u.tipo]?.etichetta ?? u.tipo,
          u.scadenza_account ? giorno(u.scadenza_account) : '',
          u.sedi.find((x) => x.principale)?.nome ?? '', u.sedi.filter((x) => !x.principale).map((x) => x.nome).join(', '),
          u.incarichi.map(incarico).join('; '), STATI[u.stato]?.etichetta ?? u.stato, u.mfa_attiva ? 'sì' : 'no',
          RECUPERO[u.email_recupero ?? 'mancante'].etichetta,
          u.ultimo_accesso ? new Date(u.ultimo_accesso).toLocaleString('it-IT') : 'mai']))
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setScaricando(false)
    }
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}

      <div className="riga riga-comandi">
        {gestisce && (
          <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setPercorso({ passo: 'dati' })}>
            Aggiungi una persona
          </Bottone>
        )}
        {/* L'importazione assegna anche i ruoli: serve poterli assegnare. */}
        {gestisce && assegna && <Bottone onClick={() => setImporta(true)}>Importa da foglio</Bottone>}
        <Bottone inCorso={scaricando} onClick={() => void scaricaElenco()}>Scarica l’elenco</Bottone>
      </div>
      {importa && (
        <ImportaUtenti chiudi={() => setImporta(false)} importati={carica} />
      )}
      {daDisattivare && (
        <DisattivaUtente id={daDisattivare} chiudi={() => setDaDisattivare(null)}
                         fatto={(testo) => { setDaDisattivare(null); setMessaggio(testo); carica() }} />
      )}
      {daAzzerare && (
        <AzzeraSecondoFattore utente={daAzzerare} chiudi={() => setDaAzzerare(null)}
                              fatto={(testo) => { setDaAzzerare(null); setMessaggio(testo); carica() }} />
      )}
      {daEliminare && (
        <EliminaDallElenco utente={daEliminare} chiudi={() => setDaEliminare(null)}
                           fatto={(testo) => { setDaEliminare(null); setMessaggio(testo); carica() }} />
      )}
      {daRiattivare && (
        <RiattivaUtente utente={daRiattivare} chiudi={() => setDaRiattivare(null)}
                        riattivato={(esito) => {
                          setDaRiattivare(null)
                          carica()
                          const utente = { id: esito.id, nome: esito.nome, cognome: esito.cognome }
                          const riattivazione = { precedenti: esito.incarichi_precedenti }
                          setPercorso(assegna
                            ? { passo: 'incarichi', utente, invito: esito, riattivazione }
                            : { passo: 'consegna', utente, invito: esito, riattivazione })
                        }} />
      )}

      {percorso?.passo === 'dati' && gestisce && (
        <Modale titolo={percorso.storico ? 'Dai l’accesso · passo 1 di 3' : 'Aggiungi una persona · passo 1 di 3'}
                larga chiudi={chiudiDati}
                azioni={<>
                  <Bottone variante="discreto" onClick={chiudiDati}>Annulla</Bottone>
                  <Bottone variante="primario" inCorso={inCorso} disabled={mancaNuovo.length > 0}
                           onClick={() => void invita()}>
                    Salva e prepara l’invito
                  </Bottone>
                </>}>
          {percorso.storico
            ? (
              <p className="nota-modulo">
                È un autore del vecchio archivio: la sua utenza resta la stessa, con le schede e gli
                interventi che aveva registrato. Controlla nome, cognome ed email, scegli il rapporto
                con CIDIS e la sede. Salvando si prepara l’invito; dopo scegli che cosa potrà fare.
              </p>
            )
            : (
              <p className="nota-modulo">
                Prima i suoi dati. Salvando si prepara l’invito; dopo scegli che cosa potrà fare e come
                fargli arrivare l’invito.
              </p>
            )}
          <div ref={radiceInvito}>
            <CampiAnagrafici modulo={nuovo} nuovo catalogoSedi={sediAttive} catalogoLingue={lingue} errori={erroriInvito}
                             utenteCasella={percorso.storico?.id}
                             cambia={(parziale) => setNuovo((n) => ({ ...n, ...parziale }))} />
            {mancaNuovo.length > 0 && <p className="nota-modulo">Mancano ancora: {mancaNuovo.join(', ')}.</p>}
            <AvvisoModulo errore={erroreInvito} errori={erroriInvito} etichette={ETICHETTE_UTENTE} />
          </div>
        </Modale>
      )}
      {percorso?.passo === 'incarichi' && (
        <PannelloIncarichi utente={percorso.utente}
                           passo={percorso.riattivazione
                             ? `Riattiva ${percorso.utente.nome} ${percorso.utente.cognome} · passo 2 di 3`
                             : percorso.storico
                               ? `Dai l’accesso a ${percorso.utente.nome} ${percorso.utente.cognome} · passo 2 di 3`
                               : 'Aggiungi una persona · passo 2 di 3'}
                           promemoria={percorso.riattivazione
                             ? (
                               <Avviso tipo="informazione">
                                 {percorso.riattivazione.precedenti.length
                                   ? <>Prima della disattivazione aveva:{' '}
                                     {percorso.riattivazione.precedenti.map(descriviIncarico).join('; ')}.
                                     Gli incarichi non tornano da soli: assegna di nuovo quelli che servono.</>
                                   : 'Prima della disattivazione non aveva incarichi in essere.'}
                               </Avviso>
                             )
                             : percorso.storico && (
                               <Avviso tipo="informazione">
                                 È l’utenza di un autore del vecchio archivio: le schede e gli interventi che
                                 aveva registrato restano suoi. Assegna gli incarichi che servono.
                               </Avviso>
                             )}
                           chiudi={() => setPercorso({ ...percorso, passo: 'consegna' })}
                           cambiato={(testo) => { setMessaggio(testo); carica() }} />
      )}
      {percorso?.passo === 'consegna' && (
        <ConsegnaInvito utente={percorso.utente} invito={percorso.invito} chiudi={() => setPercorso(null)}
                        titolo={percorso.riattivazione
                          ? `Riattiva ${percorso.utente.nome} ${percorso.utente.cognome} · consegna l’invito`
                          : percorso.storico
                            ? `Dai l’accesso a ${percorso.utente.nome} ${percorso.utente.cognome} · consegna l’invito`
                            : percorso.recupero
                              ? `Nuovo invito per ${percorso.utente.nome} ${percorso.utente.cognome} · recupero della password`
                              : undefined}
                        nota={percorso.riattivazione && !assegna
                          ? 'Gli incarichi revocati con la disattivazione non tornano da soli: chiedi di '
                            + 'riassegnarli a chi gestisce gli incarichi.'
                          : percorso.recupero
                            ? 'Non ha un’email di recupero verificata: consegna collegamento e codice per un’altra via, '
                              + 'per esempio per telefono. Accettando l’invito sceglie la password nuova; le altre sessioni '
                              + 'si chiudono e il secondo fattore, se attivo, resta.'
                            : undefined} />
      )}

      <div className="griglia-filtri operatori-filtri">
        <Campo etichetta="Cerca" tipo="search" valore={cerca} cambia={setCerca}
               segnaposto="Nome, cognome o email" />
        <Scelta etichetta="Rapporto con CIDIS" vuota="Tutti" valore={rapporto}
                cambia={(v) => { setRapporto(v); setSalta(0) }}
                opzioni={Object.entries(RAPPORTI).map(([valore, r]) => ({ valore, etichetta: r.etichetta }))} />
        <Scelta etichetta="Sede CIDIS" vuota="Tutte" valore={sede}
                cambia={(v) => { setSede(v); setSalta(0) }}
                opzioni={[
                  { valore: 'nessuna', etichetta: 'Nessuna sede' },
                  ...sediAttive.map((s) => ({ valore: s.id, etichetta: s.nome })),
                ]} />
        <Scelta etichetta="Stato" vuota="Tutti, anche storici e disattivati" valore={stato}
                cambia={(v) => { setStato(v); setSalta(0) }}
                opzioni={[
                  { valore: 'in_uso', etichetta: 'In uso (attivi e invitati)' },
                  { valore: 'attivo', etichetta: 'Attivi' },
                  { valore: 'invitato', etichetta: 'Invitati' },
                  { valore: 'disattivato', etichetta: 'Disattivati e storici' },
                  { valore: 'eliminato', etichetta: 'Eliminati dall’elenco' },
                ]} />
        {gestisce && (
          <Scelta etichetta="Email di recupero" vuota="Tutti" valore={recupero}
                  cambia={(v) => { setRecupero(v); setSalta(0) }}
                  opzioni={[
                    { valore: 'mancante', etichetta: 'Senza email di recupero verificata' },
                    { valore: 'verificata', etichetta: 'Con email di recupero verificata' },
                  ]} />
        )}
      </div>

      {!utenti ? <Attesa testo="Caricamento…" /> : (
        <Riquadro denso>
          <Tabella intestazioni={['Utente', 'Rapporto con CIDIS', 'Sedi CIDIS', 'Incarichi', 'Stato',
                                  ...(gestisce ? ['Email di recupero'] : []),
                                  'Ultimo accesso', ...(gestisce ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
                   vuoto={<Vuoto testo="Nessun utente corrisponde ai criteri." />}>
            {utenti.map((u) => (
              <tr key={u.id}>
                <td data-etichetta="Utente">
                  <div className="voce-principale">{u.cognome} {u.nome}</div>
                  <div className="piccolo tenue">{u.email}</div>
                </td>
                <td data-etichetta="Rapporto con CIDIS">
                  <Etichetta tono={RAPPORTI[u.tipo]?.tono ?? 'neutro'}>
                    {RAPPORTI[u.tipo]?.etichetta ?? u.tipo}
                  </Etichetta>
                  {/* La scadenza si mostra per chiunque l'abbia: un'utenza scaduta non accede. */}
                  {u.scadenza_account
                    ? u.scadenza_account.slice(0, 10) < traGiorni(0)
                      ? <div><Etichetta tono="critico">Utenza scaduta il {giorno(u.scadenza_account)}</Etichetta></div>
                      : <div className="piccolo tenue">fino al {giorno(u.scadenza_account)}</div>
                    : u.tipo === 'esterno' && <div className="piccolo tenue">senza scadenza</div>}
                </td>
                <td data-etichetta="Sedi CIDIS">
                  {u.sedi.length === 0
                    ? tecnico(u.tipo)
                      ? <span className="tenue piccolo">—</span>
                      : <Etichetta tono="attenzione">Nessuna sede</Etichetta>
                    : (
                      <div className="riga-etichette">
                        {u.sedi.map((s) => (
                          <Etichetta key={s.id} tono={s.principale ? 'accento' : 'neutro'}>
                            {s.nome}{s.principale ? ' · principale' : ''}
                          </Etichetta>
                        ))}
                      </div>
                    )}
                </td>
                <td data-etichetta="Incarichi">
                  <div className="riga-etichette">
                    {u.incarichi.length === 0 && <span className="tenue piccolo">Nessun incarico</span>}
                    {u.incarichi.map((i) => (
                      <Etichetta key={i.id} tono="accento">
                        <span title={i.ruolo}>{nomiRuoli[i.ruolo] ?? i.ruolo}</span>
                        {i.ambito_id ? ` · ${(AMBITI_ETICHETTE[i.ambito_tipo] ?? i.ambito_tipo).toLowerCase()}` : ''}
                      </Etichetta>
                    ))}
                  </div>
                </td>
                <td data-etichetta="Stato">
                  <div className="riga-etichette">
                    <Etichetta tono={STATI[u.stato]?.tono ?? 'neutro'}>{STATI[u.stato]?.etichetta ?? u.stato}</Etichetta>
                    {u.mfa_attiva && <Etichetta>Secondo fattore</Etichetta>}
                    {/* Chi deve ancora attivarlo (493): obbligo in vigore, oppure la data in cui lo sara'. */}
                    {!u.mfa_attiva && u.mfa_obbligatoria_dal && u.stato !== 'disattivato' && u.tipo !== 'storico' && (
                      u.mfa_obbligatoria_dal <= traGiorni(0)
                        ? <Etichetta tono="attenzione">Secondo fattore da attivare</Etichetta>
                        : <Etichetta tono="neutro">Secondo fattore entro il {giorno(giornoPrima(u.mfa_obbligatoria_dal))}</Etichetta>
                    )}
                  </div>
                  {u.eliminato_il
                    ? <div className="piccolo tenue">eliminato dall’elenco il {giorno(u.eliminato_il)}</div>
                    : u.disattivato_il && (u.stato === 'disattivato' || u.stato === 'sospeso') && (
                      <div className="piccolo tenue">dal {giorno(u.disattivato_il)}</div>
                    )}
                </td>
                {gestisce && (
                  <td data-etichetta="Email di recupero">
                    {tecnico(u.tipo)
                      ? <span className="tenue piccolo">—</span>
                      : <Etichetta tono={RECUPERO[u.email_recupero ?? 'mancante'].tono}>
                        {RECUPERO[u.email_recupero ?? 'mancante'].etichetta}
                      </Etichetta>}
                    {u.recupero_da_invitare && (
                      <div className="piccolo tenue">recupero approvato: prepara un nuovo invito</div>
                    )}
                  </td>
                )}
                <td data-etichetta="Ultimo accesso" className="piccolo">
                  {u.ultimo_accesso ? new Date(u.ultimo_accesso).toLocaleString('it-IT') : 'Mai'}
                </td>
                {gestisce && (
                  <td data-etichetta="Azioni">
                    <div className="riga-etichette">
                      <Bottone dimensione="piccolo" aria-label={`Modifica ${u.cognome} ${u.nome}`}
                               onClick={() => setDaModificare(u.id)}>
                        Modifica
                      </Bottone>
                      {/* Un autore del vecchio archivio riceve incarichi solo dopo l'accesso (468). */}
                      {assegna && u.tipo !== 'storico' && (
                        <Bottone dimensione="piccolo" aria-label={`Incarichi di ${u.cognome} ${u.nome}`}
                                 onClick={() => setIncarichiDi(u)}>
                          Incarichi
                        </Bottone>
                      )}
                      {u.tipo === 'storico' && !u.eliminato_il && (
                        <Bottone dimensione="piccolo" aria-label={`Dai l’accesso a ${u.cognome} ${u.nome}`}
                                 onClick={() => daiAccesso(u)}>
                          Dai l’accesso
                        </Bottone>
                      )}
                      {/* Gli autori del vecchio archivio non si cancellano: firmano il loro lavoro (469). */}
                      {u.tipo === 'storico' && !u.eliminato_il && (
                        <Bottone dimensione="piccolo" variante="critico" aria-label={`Elimina dall’elenco ${u.cognome} ${u.nome}`}
                                 onClick={() => { setMessaggio(null); setDaEliminare(u) }}>
                          Elimina dall’elenco
                        </Bottone>
                      )}
                      {u.eliminato_il && (
                        <Bottone dimensione="piccolo" inCorso={ripristino === u.id}
                                 aria-label={`Ripristina nell’elenco ${u.cognome} ${u.nome}`}
                                 onClick={() => void ripristina(u)}>
                          Ripristina nell’elenco
                        </Bottone>
                      )}
                      {/* Impersonazione in sola lettura (524): solo per chi puo', sugli utenti attivi. */}
                      {io?.puoImpersonare && u.stato === 'attivo' && u.id !== io.id && (
                        <Bottone dimensione="piccolo" inCorso={vediCome === u.id}
                                 aria-label={`Vedi il gestionale come ${u.cognome} ${u.nome}`}
                                 title="Vedi il gestionale come lo vede questo utente, in sola lettura"
                                 onClick={() => {
                                   setMessaggio(null); setErrore(null); setVediCome(u.id)
                                   vediComeUtente(u.id).catch((e) => { setVediCome(null); setErrore(messaggioErrore(e)) })
                                 }}>
                          Vedi come
                        </Bottone>
                      )}
                      {/* Anche per chi e' gia' entrato, dopo una sua richiesta di recupero approvata (520). */}
                      {(u.stato === 'invitato' || u.recupero_da_invitare) && (
                        <Bottone dimensione="piccolo" inCorso={rinnovo === u.id}
                                 aria-label={`Nuovo invito per ${u.cognome} ${u.nome}`}
                                 onClick={() => void rinnovaInvito(u)}>
                          Nuovo invito
                        </Bottone>
                      )}
                      {/* Gli autori del vecchio archivio non hanno mai avuto accesso; la propria
                          utenza non si disattiva. */}
                      {u.tipo !== 'storico' && u.id !== io?.id && u.stato !== 'disattivato' && u.stato !== 'sospeso' && (
                        <Bottone dimensione="piccolo" variante="critico" aria-label={`Disattiva ${u.cognome} ${u.nome}`}
                                 onClick={() => { setMessaggio(null); setDaDisattivare(u.id) }}>
                          Disattiva
                        </Bottone>
                      )}
                      {/* Il proprio no: con la sola password ci si toglierebbe il secondo fattore (493). */}
                      {u.mfa_attiva && u.id !== io?.id && (
                        <Bottone dimensione="piccolo" variante="critico"
                                 aria-label={`Azzera il secondo fattore di ${u.cognome} ${u.nome}`}
                                 onClick={() => { setMessaggio(null); setDaAzzerare(u) }}>
                          Azzera il secondo fattore
                        </Bottone>
                      )}
                      {u.tipo !== 'storico' && (u.stato === 'disattivato' || u.stato === 'sospeso') && (
                        <Bottone dimensione="piccolo" aria-label={`Riattiva ${u.cognome} ${u.nome}`}
                                 onClick={() => { setMessaggio(null); setDaRiattivare({ id: u.id, nome: u.nome, cognome: u.cognome }) }}>
                          Riattiva
                        </Bottone>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={salta} limite={LIMITE} totale={totaleDi(utenti)}
                       ricevute={utenti.length} cambia={setSalta} />
        </Riquadro>
      )}

      {daModificare && (
        <ModificaUtente id={daModificare} catalogoSedi={sediAttive} catalogoLingue={lingue}
                        chiudi={() => setDaModificare(null)}
                        salvato={(testo) => { setDaModificare(null); setMessaggio(testo); carica() }} />
      )}

      {incarichiDi && (
        <PannelloIncarichi utente={incarichiDi} chiudi={() => setIncarichiDi(null)}
                           cambiato={(testo) => { setMessaggio(testo); carica() }} />
      )}
    </>
  )
}

// --- modifica ------------------------------------------------------------------------------------

function ModificaUtente({ id, catalogoSedi, catalogoLingue, chiudi, salvato }: {
  id: string
  catalogoSedi: VoceSede[]
  catalogoLingue: Lingua[]
  chiudi: () => void
  salvato: (messaggio: string) => void
}) {
  const [scheda, setScheda] = useState<SchedaUtente | null>(null)
  const [modulo, setModulo] = useState<Modulo>(MODULO_VUOTO)
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [daAdeguare, setDaAdeguare] = useState<IncaricoDaAdeguare[] | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [erroreSalvataggio, setErroreSalvataggio] = useState<string | null>(null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [tentativo, setTentativo] = useState(0)
  // La casella cidisonlus.org in deroga (533) e' una casella di gruppo: l'elenco sotto si ricarica.
  const [caselleVersione, setCaselleVersione] = useState(0)
  const radice = useRef<HTMLDivElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  useEffect(() => {
    api.get<SchedaUtente>(`/amministrazione/utenti/${id}`)
      .then((s) => {
        setScheda(s)
        setModulo({
          ...MODULO_VUOTO,
          nome: s.nome, cognome: s.cognome, email: s.email, tipo: s.tipo,
          scadenza_account: s.scadenza_account ?? '', data_nascita: s.data_nascita ?? '',
          comune: s.comune_residenza_domicilio ?? '',
          comuneTesto: s.comune_nome ? `${s.comune_nome} (${s.comune_provincia ?? ''})` : '',
          paese_origine: s.paese_origine ?? '',
          lingue: s.lingue.map((l) => l.codice),
          sedi: s.sedi.map((x) => x.id),
          sede_principale: s.sedi.find((x) => x.principale)?.id ?? '',
        })
      })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [id])

  const cambiaRapporto = scheda !== null && modulo.tipo !== scheda.tipo
  const manca = [
    ...mancanze(modulo, false, scheda?.accesso_esterni ?? false),
    ...(cambiaRapporto && motivo.trim().length < 5 ? ['motivo della variazione del rapporto'] : []),
  ]

  // Sedi e lingue gia' registrate restano visibili anche se non piu' attive nel catalogo.
  const sedi: VoceSede[] = [
    ...catalogoSedi,
    ...(scheda?.sedi ?? []).filter((s) => !catalogoSedi.some((c) => c.id === s.id))
      .map((s) => ({ id: s.id, nome: `${s.nome} (non attiva)` })),
  ]
  const lingue: Lingua[] = [
    ...catalogoLingue,
    ...(scheda?.lingue ?? []).filter((l) => !catalogoLingue.some((c) => c.codice_iso === l.codice))
      .map((l) => ({ codice_iso: l.codice, nome: l.nome })),
  ]

  // Ogni cambiamento del modulo invalida l'elenco degli incarichi da adeguare gia' mostrato.
  const cambia = (parziale: Partial<Modulo>) => {
    setDaAdeguare(null)
    setModulo((m) => ({ ...m, ...parziale }))
  }

  async function salva(adegua: boolean) {
    if (!scheda) return
    setErrore(null); setErroreSalvataggio(null); setErrori({}); setInCorso(true)
    try {
      const esito = await api.patch<{ incarichi_adeguati: number }>(`/amministrazione/utenti/${id}`, {
        ...corpoAnagrafico(modulo, { tipo: scheda.tipo, scadenza_account: scheda.scadenza_account }),
        ...(cambiaRapporto ? { tipo: modulo.tipo, motivo_rapporto: motivo.trim() } : {}),
        adegua_incarichi: adegua,
      })
      salvato(esito.incarichi_adeguati
        ? `Utente aggiornato. Incarichi adeguati al nuovo rapporto: ${esito.incarichi_adeguati}.`
        : 'Utente aggiornato.')
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 'INCARICHI_DA_ADEGUARE') {
        setDaAdeguare((e.corpo as { incarichi: IncaricoDaAdeguare[] }).incarichi)
      } else {
        setErroreSalvataggio(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
        setErrori(erroriDellApi(e, CODICI_CAMPO_UTENTE))
        setTentativo((n) => n + 1)
      }
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Modifica utente" larga chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          {daAdeguare
            ? (
              <Bottone variante="critico" inCorso={inCorso} disabled={manca.length > 0}
                       onClick={() => void salva(true)}>
                Adegua gli incarichi e salva
              </Bottone>
            )
            : (
              <Bottone variante="primario" inCorso={inCorso} disabled={!scheda || manca.length > 0}
                       onClick={() => void salva(false)}>
                Salva
              </Bottone>
            )}
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!scheda ? (!errore && <Attesa testo="Caricamento…" />) : (
        <div ref={radice}>
          <p className="piccolo tenue">{scheda.email}</p>
          {/* Email di recupero (520): la imposta e la cambia la persona da «Il mio profilo». */}
          <p className="piccolo">
            Email di recupero:{' '}
            {scheda.email_recupero
              ? <>{scheda.email_recupero.indirizzo}{' '}
                <span className="tenue">
                  {scheda.email_recupero.verificata_il ? `· verificata il ${giorno(scheda.email_recupero.verificata_il)}` : '· da verificare'}
                </span></>
              : <span className="tenue">nessuna</span>}
            {scheda.email_recupero_nuova_dal && <span className="tenue"> · un indirizzo nuovo aspetta la conferma</span>}
            <span className="tenue"> · la cambia solo la persona, da «Il mio profilo».</span>
          </p>
          <CampiAnagrafici modulo={modulo} nuovo={false} cambia={cambia} errori={errori}
                           collaboratore={scheda.accesso_esterni ?? false}
                           catalogoSedi={sedi} catalogoLingue={lingue} rapportoRegistrato={scheda.tipo}
                           scadenzaFacoltativa={scheda.tipo !== 'esterno' && scheda.scadenza_account !== null}
                           paeseRegistrato={scheda.paese_origine && scheda.paese_nome
                             ? { codice: scheda.paese_origine, nome: scheda.paese_nome } : undefined} />

          {cambiaRapporto && (
            <div className="operatori-sezione">
              <Avviso tipo="attenzione">
                {modulo.tipo === 'esterno'
                  ? 'Da interno a esterno: l’utenza avrà una scadenza. Gli incarichi con ruoli riservati al '
                    + 'personale interno saranno revocati; gli altri, se senza scadenza o con scadenza '
                    + 'successiva, si accorceranno fino a quella data. L’elenco compare prima della conferma.'
                  : 'Da esterno a interno: la scadenza dell’utenza si toglie e gli incarichi con ruoli '
                    + 'riservati agli esterni saranno revocati, previa conferma.'}
              </Avviso>
              <AreaTesto etichetta="Motivo della variazione del rapporto" obbligatorio righe={3}
                         maxLength={500} valore={motivo} errore={errori.motivo_rapporto}
                         cambia={(v) => { setDaAdeguare(null); setMotivo(v) }} />
            </div>
          )}

          {daAdeguare && (
            <Avviso tipo="attenzione">
              Il nuovo rapporto con CIDIS richiede di adeguare questi incarichi:
              <ul className="elenco-avviso">
                {daAdeguare.map((i) => (
                  <li key={i.id}>
                    {i.ruolo} · {i.ambito_tipo}
                    {i.azione === 'revoca' ? ': revoca' : `: scadenza al ${giorno(i.nuova_scadenza)}`}
                  </li>
                ))}
              </ul>
            </Avviso>
          )}

          {manca.length > 0 && <p className="nota-modulo">Da completare: {manca.join(', ')}.</p>}

          {scheda.accesso_esterni && (
            <DerogheEsterno utenteId={id} nome={scheda.nome} cognome={scheda.cognome}
                            casella={scheda.casella_personale ?? null} creata={() => setCaselleVersione((n) => n + 1)} />
          )}

          <CaselleDiGruppoUtente key={caselleVersione} utenteId={id} servizio={scheda.accesso_esterni} />

          {scheda.variazioni_rapporto.length > 0 && (
            <section className="operatori-sezione">
              <h3 className="operatori-titoletto">Variazioni del rapporto con CIDIS</h3>
              <ul className="operatori-variazioni">
                {scheda.variazioni_rapporto.map((v) => (
                  <li key={`${v.variata_il} ${v.tipo_nuovo}`}>
                    <span className="mono">{giorno(v.variata_il)}</span>
                    {' · '}{RAPPORTI[v.tipo_precedente]?.etichetta} → {RAPPORTI[v.tipo_nuovo]?.etichetta}
                    {' · '}{v.variata_da}
                    {v.incarichi_adeguati > 0 && ` · incarichi adeguati: ${v.incarichi_adeguati}`}
                    <div className="tenue">{v.motivazione}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!!scheda.variazioni_stato?.length && (
            <section className="operatori-sezione">
              <h3 className="operatori-titoletto">Disattivazioni e riattivazioni</h3>
              <ul className="operatori-variazioni">
                {scheda.variazioni_stato.map((v) => (
                  <li key={`${v.variata_il} ${v.azione}`}>
                    <span className="mono">{giorno(v.variata_il)}</span>
                    {' · '}{v.azione === 'disattivazione' ? 'Disattivata'
                      : v.azione === 'accesso_storico' ? 'Accesso dato all’autore del vecchio archivio, con un invito'
                        : v.azione === 'eliminazione_elenco' ? 'Eliminato dall’elenco'
                          : v.azione === 'ripristino_elenco' ? 'Ripristinato nell’elenco'
                            : 'Riattivata con un nuovo invito'}
                    {' · '}{v.variata_da}
                    {v.azione === 'disattivazione' && v.incarichi_revocati > 0 && ` · incarichi revocati: ${v.incarichi_revocati}`}
                    {v.motivazione && <div className="tenue">{v.motivazione}</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Esito del salvataggio in coda al corpo, subito sopra i comandi della finestra. */}
          <AvvisoModulo errore={erroreSalvataggio} errori={errori} etichette={ETICHETTE_UTENTE} />
        </div>
      )}
    </Modale>
  )
}

// --- incarichi ------------------------------------------------------------------------------------

type IncaricoDettaglio = {
  id: string; ruolo_codice: string; ruolo_nome: string; vede_dati_persone: boolean
  ambito_tipo: string; ambito_id: string | null; ambito_nome: string | null
  solo_propri: boolean; motivazione: string | null; valida_a: string | null; creata_il: string
  revocato: boolean; approvazione_id: string | null; scadenza_max_giorni: number | null
}
type RuoloCatalogo = {
  codice: string; nome: string; ambiti_ammessi: string[]; ambito_obbligatorio: boolean
  vede_dati_persone: boolean; scadenza_max_giorni: number | null; assegnabile: boolean
}
/** Le sedi portano anche la sede madre: gli sportelli si elencano sotto la loro sede (463). */
type VoceAmbito = { id: string; nome: string; sede_padre_id?: string | null; sede_padre_nome?: string | null }

/** Risposta dell'assegnazione su piu' sedi: un incarico per sede (28/10/2026). */
type EsitoSedi = {
  creati: { id: string; sede_id: string }[]
  in_approvazione: { approvazione_id: string; sede_id: string }[]
  gia_presenti: string[]
}

/** Esito dell'assegnazione su piu' sedi: incarichi creati, sedi gia' coperte, sedi in approvazione. */
function esitoSuPiuSedi(ruolo: string, chi: string, e: EsitoSedi): string {
  const creati = e.creati.length
  const gia = e.gia_presenti.length
  const inApprovazione = e.in_approvazione.length
  const parti: string[] = []
  if (!creati && !inApprovazione) parti.push('nessun incarico nuovo')
  if (creati) parti.push(creati === 1 ? '1 incarico creato' : `${creati} incarichi creati, uno per sede`)
  if (gia) parti.push(gia === 1 ? '1 sede era già coperta' : `${gia} sedi erano già coperte`)
  if (inApprovazione) {
    parti.push(inApprovazione === 1
      ? '1 sede è fuori dal tuo perimetro: è stata aperta una richiesta di approvazione a chi può'
      : `${inApprovazione} sedi sono fuori dal tuo perimetro: per ciascuna è stata aperta una richiesta `
        + 'di approvazione a chi può')
  }
  return `Incarico ${ruolo} a ${chi}: ${parti.join('; ')}.`
}

const AMBITI_ETICHETTE: Record<string, string> = {
  organizzazione: 'Tutta CIDIS', sede: 'Sede CIDIS', area: 'Area',
  progetto: 'Progetto', ente: 'Ente', tipologia: 'Tipologia di intervento',
}

/**
 * Quali schede apre l'incarico, detto per l'ambito scelto. Il testo di prima («si aprono le schede
 * delle persone dove vale l'incarico») non si capiva (Direzione, 19/10/2026).
 */
function schedeDellAmbito(ruolo: string, ambito: string): string {
  const chi = `Con questo incarico, ${ruolo} vede e lavora`
  switch (ambito) {
    case 'organizzazione': return `${chi} le schede di tutte le persone seguite da CIDIS.`
    case 'sede': return `${chi} solo le schede delle persone seguite dalle sedi scelte e dai loro sportelli.`
    case 'area': return `${chi} solo le schede delle persone che hanno un percorso nell’area scelta.`
    case 'progetto': return `${chi} solo le schede delle persone che hanno un percorso nel progetto scelto.`
    case 'tipologia': return `${chi} solo le schede delle persone che hanno un percorso di quella tipologia.`
    default: return `${chi} solo le schede delle persone che rientrano nell’ambito che scegli qui sotto: `
      + 'una sede, un’area o un progetto. Le altre schede non le vede.'
  }
}

/**
 * Incarichi di un utente: elenco, assegnazione, proroga e revoca (OPE-11).
 *
 * L'incarico e' l'atto che apre l'accesso ai dati, non un attributo dell'anagrafica: sta in una
 * finestra propria, con la motivazione obbligatoria in ogni verso, gli avvisi per i ruoli che
 * aprono le schede delle persone e per l'autoassegnazione (Q-H10). L'API risponde 202 quando chi
 * assegna non copre l'ambito: l'atto diventa una richiesta di approvazione, e il messaggio lo dice.
 */
function PannelloIncarichi({ utente, chiudi, cambiato, passo, promemoria }: {
  utente: { id: string; nome: string; cognome: string }
  chiudi: () => void
  cambiato: (testo: string) => void
  /** Dentro il percorso «Aggiungi una persona»: titolo del passo, e si va avanti invece di chiudere. */
  passo?: string
  /** Riattivazione (464): gli incarichi che la persona aveva prima. */
  promemoria?: ReactNode
}) {
  const { io } = useSessione()
  const [incarichi, setIncarichi] = useState<IncaricoDettaglio[] | null>(null)
  const [ruoli, setRuoli] = useState<RuoloCatalogo[]>([])
  const [ambiti, setAmbiti] = useState<VoceAmbito[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const [ruolo, setRuolo] = useState('')
  const [ambitoTipo, setAmbitoTipo] = useState('')
  const [ambitoId, setAmbitoId] = useState('')
  // Ambito di sede: piu' sedi insieme, o tutte, e un incarico per ciascuna (28/10/2026).
  const [sediScelte, setSediScelte] = useState<string[]>([])
  const [tutteLeSedi, setTutteLeSedi] = useState(false)
  const [soloPropri, setSoloPropri] = useState(false)
  const [validaA, setValidaA] = useState('')
  const [motivazione, setMotivazione] = useState('')
  const [daRevocare, setDaRevocare] = useState<IncaricoDettaglio | null>(null)
  const [daProrogare, setDaProrogare] = useState<IncaricoDettaglio | null>(null)
  const [motivo, setMotivo] = useState('')
  const [nuovaScadenza, setNuovaScadenza] = useState('')

  const carica = useCallback(async () => {
    try {
      setIncarichi(await api.get<IncaricoDettaglio[]>(
        `/amministrazione/incarichi?utente_id=${utente.id}`))
    } catch (e) {
      setErrore(messaggioErrore(e))
    }
  }, [utente.id])

  useEffect(() => { void carica() }, [carica])
  useEffect(() => {
    api.get<{ ruoli: RuoloCatalogo[] }>('/amministrazione/ruoli')
      .then((r) => setRuoli(r.ruoli)).catch(() => {})
  }, [])

  const scelto = ruoli.find((r) => r.codice === ruolo)

  // Il tipo di ambito segue il ruolo: un ruolo che vale solo sull'organizzazione non deve
  // proporre una tendina di sedi.
  useEffect(() => {
    if (!scelto) { setAmbitoTipo(''); return }
    setAmbitoTipo((corrente) => (scelto.ambiti_ammessi.includes(corrente)
      ? corrente
      : scelto.ambiti_ammessi[0] ?? ''))
  }, [scelto])

  useEffect(() => {
    setAmbitoId(''); setSediScelte([]); setTutteLeSedi(false)
    if (!ambitoTipo || ambitoTipo === 'organizzazione') { setAmbiti([]); return }
    api.get<VoceAmbito[]>(`/amministrazione/ambiti?tipo=${ambitoTipo}`)
      .then(setAmbiti).catch(() => setAmbiti([]))
  }, [ambitoTipo])

  const seStesso = utente.id === io?.id
  const scadenzaObbligatoria = !!scelto?.scadenza_max_giorni
  const piuSedi = ambitoTipo === 'sede'
  const manca = [
    !ruolo && 'ruolo',
    piuSedi
      ? !tutteLeSedi && !sediScelte.length && 'sedi'
      : ambitoTipo && ambitoTipo !== 'organizzazione' && !ambitoId && AMBITI_ETICHETTE[ambitoTipo]?.toLowerCase(),
    scadenzaObbligatoria && !validaA && 'scadenza',
    motivazione.trim().length < 5 && 'motivazione',
  ].filter(Boolean) as string[]

  async function assegna() {
    setErrore(null); setEsito(null); setInCorso(true)
    try {
      const comune = {
        utente_id: utente.id,
        ruolo_codice: ruolo,
        solo_propri: soloPropri,
        valida_a: validaA || null,
        motivazione: motivazione.trim(),
      }
      let testo: string
      if (piuSedi) {
        // Un incarico per sede: l'API dice quali sono nati, quali c'erano gia' e quali aspettano
        // l'approvazione di chi copre la sede.
        const risposta = await api.post<EsitoSedi>('/amministrazione/incarichi', {
          ...comune, ambito_tipo: 'sede',
          ...(tutteLeSedi ? { tutte_le_sedi: true } : { ambito_ids: sediScelte }),
        })
        testo = esitoSuPiuSedi(ruolo, `${utente.nome} ${utente.cognome}`, risposta)
      } else {
        const risposta = await api.post<{ in_approvazione: boolean; autoassegnazione?: boolean }>(
          '/amministrazione/incarichi', {
            ...comune,
            ambito_tipo: ambitoTipo || 'organizzazione',
            ambito_id: ambitoTipo && ambitoTipo !== 'organizzazione' ? ambitoId : null,
          })
        testo = risposta.in_approvazione
          ? 'Non puoi assegnare su questo ambito: è stata aperta una richiesta di approvazione a chi può.'
          : `Incarico ${ruolo} assegnato a ${utente.nome} ${utente.cognome}.`
      }
      setEsito(testo)
      setRuolo(''); setAmbitoId(''); setSediScelte([]); setTutteLeSedi(false)
      setValidaA(''); setMotivazione(''); setSoloPropri(false)
      await carica()
      cambiato(testo)
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  async function revoca() {
    if (!daRevocare) return
    setErrore(null); setEsito(null); setInCorso(true)
    try {
      await api.elimina(`/amministrazione/incarichi/${daRevocare.id}`, { motivo: motivo.trim() })
      const testo = `Incarico ${daRevocare.ruolo_codice} revocato.`
      setEsito(testo); setDaRevocare(null); setMotivo('')
      await carica()
      cambiato(testo)
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  async function proroga() {
    if (!daProrogare) return
    setErrore(null); setEsito(null); setInCorso(true)
    try {
      await api.patch(`/amministrazione/incarichi/${daProrogare.id}`, {
        valida_a: nuovaScadenza, motivo: motivo.trim(),
      })
      const testo = `Scadenza dell’incarico ${daProrogare.ruolo_codice} spostata al ${giorno(nuovaScadenza)}.`
      setEsito(testo); setDaProrogare(null); setMotivo(''); setNuovaScadenza('')
      await carica()
      cambiato(testo)
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={passo ?? `Incarichi di ${utente.nome} ${utente.cognome}`} chiudi={chiudi} larga
            azioni={passo
              ? <Bottone variante="primario" onClick={chiudi}>Avanti: consegna l’invito</Bottone>
              : <Bottone variante="discreto" onClick={chiudi}>Chiudi</Bottone>}>
      {passo && (
        <p className="nota-modulo">
          Che cosa potrà fare {utente.nome}: scegli il ruolo e dove vale. Puoi aggiungerne altri anche
          dopo, dalla sua riga nell’elenco.
        </p>
      )}
      {promemoria}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {!incarichi ? <Attesa testo="Caricamento…" /> : incarichi.length === 0
        ? <Vuoto testo="Nessun incarico in essere: questo utente non accede ad alcun dato." />
        : (
          <Tabella intestazioni={['Ruolo', 'Ambito', 'Scadenza', '', '']}
                   vuoto={<Vuoto testo="Nessun incarico." />}>
            {incarichi.map((i) => (
              <tr key={i.id}>
                <td data-etichetta="Ruolo">
                  <div className="voce-principale">{i.ruolo_codice} · {i.ruolo_nome}</div>
                  {i.vede_dati_persone && (
                    <div><Etichetta tono="attenzione">Accede ai dati delle persone</Etichetta></div>
                  )}
                  {i.solo_propri && <div className="piccolo tenue">Solo i propri</div>}
                </td>
                <td data-etichetta="Ambito">
                  {AMBITI_ETICHETTE[i.ambito_tipo] ?? i.ambito_tipo}
                  {i.ambito_id && (
                    <div className="piccolo tenue">{i.ambito_nome ?? i.ambito_id}</div>
                  )}
                </td>
                <td data-etichetta="Scadenza">{i.valida_a ? giorno(i.valida_a) : 'Nessuna'}</td>
                <td>
                  <Bottone dimensione="piccolo"
                           aria-label={`Proroga l’incarico ${i.ruolo_codice}`}
                           onClick={() => {
                             setDaProrogare(i); setDaRevocare(null); setMotivo('')
                             setNuovaScadenza(i.valida_a ?? '')
                           }}>
                    Proroga
                  </Bottone>
                </td>
                <td>
                  <Bottone dimensione="piccolo" variante="critico"
                           aria-label={`Revoca l’incarico ${i.ruolo_codice}`}
                           onClick={() => { setDaRevocare(i); setDaProrogare(null); setMotivo('') }}>
                    Revoca
                  </Bottone>
                </td>
              </tr>
            ))}
          </Tabella>
        )}

      {daRevocare && (
        <section className="referente-blocco">
          <h3>Revoca di {daRevocare.ruolo_codice}</h3>
          <AreaTesto etichetta="Motivo della revoca" obbligatorio valore={motivo} cambia={setMotivo}
                     righe={2} aiuto="Resta nel registro degli eventi." />
          <div className="riga">
            <Bottone variante="critico" inCorso={inCorso} disabled={motivo.trim().length < 3}
                     onClick={() => void revoca()}>
              Revoca l’incarico
            </Bottone>
            <Bottone variante="discreto" onClick={() => setDaRevocare(null)}>Annulla</Bottone>
          </div>
        </section>
      )}

      {daProrogare && (
        <section className="referente-blocco">
          <h3>Proroga di {daProrogare.ruolo_codice}</h3>
          <Campo etichetta="Nuova scadenza" tipo="date" obbligatorio valore={nuovaScadenza}
                 cambia={setNuovaScadenza}
                 aiuto={daProrogare.scadenza_max_giorni
                   ? `Questo ruolo dura al massimo ${daProrogare.scadenza_max_giorni} giorni.`
                   : 'La proroga sposta la scadenza in avanti.'} />
          <AreaTesto etichetta="Motivo della proroga" obbligatorio valore={motivo} cambia={setMotivo}
                     righe={2} />
          <div className="riga">
            <Bottone variante="primario" inCorso={inCorso}
                     disabled={!nuovaScadenza || motivo.trim().length < 5}
                     onClick={() => void proroga()}>
              Proroga la scadenza
            </Bottone>
            <Bottone variante="discreto" onClick={() => setDaProrogare(null)}>Annulla</Bottone>
          </div>
        </section>
      )}

      <section className="referente-blocco">
        <h3>Assegna un incarico</h3>
        {seStesso && (
          <Avviso tipo="attenzione">
            Stai assegnando un incarico a te stesso: l’operazione è permessa, ma resta nel registro
            e fa partire un avviso a direzione, DPO e referente privacy.
          </Avviso>
        )}
        {scelto?.vede_dati_persone && (
          <Avviso tipo="attenzione">
            {schedeDellAmbito(scelto.nome, ambitoTipo)}
          </Avviso>
        )}
        <div className="griglia-filtri">
          <Scelta etichetta="Ruolo" obbligatorio valore={ruolo} cambia={setRuolo} vuota="Scegli…"
                  opzioni={ruoli.filter((r) => r.assegnabile)
                    .map((r) => ({ valore: r.codice, etichetta: `${r.nome} (${r.codice})` }))} />
          <Scelta etichetta="Tipo di ambito" obbligatorio valore={ambitoTipo} cambia={setAmbitoTipo}
                  disabilitato={!scelto}
                  aiuto={scelto ? undefined : 'Scegli prima il ruolo.'}
                  opzioni={(scelto?.ambiti_ammessi ?? []).map((a) => ({
                    valore: a, etichetta: AMBITI_ETICHETTE[a] ?? a,
                  }))} />
          {ambitoTipo && ambitoTipo !== 'organizzazione' && !piuSedi && (
            <Scelta etichetta={AMBITI_ETICHETTE[ambitoTipo] ?? 'Ambito'}
                    obbligatorio={scelto?.ambito_obbligatorio}
                    valore={ambitoId} cambia={setAmbitoId} vuota="Scegli…"
                    opzioni={ambiti.map((a) => ({ valore: a.id, etichetta: a.nome }))} />
          )}
          <Campo etichetta="Scadenza" tipo="date" valore={validaA} cambia={setValidaA}
                 obbligatorio={scadenzaObbligatoria}
                 aiuto={scelto?.scadenza_max_giorni
                   ? `Obbligatoria: questo ruolo dura al massimo ${scelto.scadenza_max_giorni} giorni.`
                   : 'Facoltativa.'} />
        </div>
        {piuSedi && (
          <fieldset className="gruppo-caselle incarico-sedi">
            <legend>
              Sedi CIDIS<span className="campo-obbligatorio" aria-hidden="true"> *</span>
            </legend>
            <Spunta etichetta="Tutte le sedi" valore={tutteLeSedi} cambia={setTutteLeSedi}
                    aiuto="Un incarico per ciascuna sede attiva; gli sportelli rientrano nella loro sede. Le sedi aperte in seguito vanno aggiunte a parte." />
            <SceltaMultipla etichetta="Sede da aggiungere" vuota="Scegli…" voci={vociSedi(ambiti)}
                            scelte={sediScelte} cambia={setSediScelte} disabilitato={tutteLeSedi}
                            massimo={50}
                            aiuto="Si crea un incarico per ciascuna sede scelta, con lo stesso ruolo, la stessa scadenza e la stessa motivazione." />
          </fieldset>
        )}
        <Spunta etichetta="Solo i propri" valore={soloPropri} cambia={setSoloPropri}
                aiuto="Limita l’incarico alle righe registrate dal titolare." />
        <AreaTesto etichetta="Motivazione" obbligatorio valore={motivazione} cambia={setMotivazione}
                   righe={2} aiuto="Resta sull’incarico e nel registro degli eventi." />
        {manca.length > 0 && <p className="nota-modulo">Da completare: {manca.join(', ')}.</p>}
        <div className="riga">
          <Bottone variante="primario" inCorso={inCorso} disabled={manca.length > 0}
                   onClick={() => void assegna()}>
            Assegna l’incarico
          </Bottone>
        </div>
      </section>
    </Modale>
  )
}

/**
 * Ultimo passo dell'invito: dice com'e' andata l'email e mette in mano le due meta' da
 * consegnare. Il collegamento si puo' mandare come si vuole; il codice si detta a voce, ed e'
 * proprio questo a impedire che entri chi trova l'email al posto del destinatario (Q-H15).
 */
/**
 * «Elimina dall'elenco» un autore del vecchio archivio (469): sparisce dagli elenchi e da «Dai
 * l'accesso», ma resta autore del suo lavoro. Si ripristina dal filtro «Eliminati dall'elenco».
 */
function EliminaDallElenco({ utente, chiudi, fatto }: {
  utente: Utente; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const nome = `${utente.nome} ${utente.cognome}`

  async function elimina() {
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/amministrazione/utenti/${utente.id}/elimina-dall-elenco`, { motivo: motivo.trim() || null })
      fatto(`${nome} non compare più fra gli utenti. Lo ritrovi con lo stato «Eliminati dall’elenco».`)
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Eliminare dall’elenco ${nome}?`} chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="critico" inCorso={inCorso} disabled={!!motivo.trim() && motivo.trim().length < 5}
                       onClick={() => void elimina()}>
                Elimina dall’elenco
              </Bottone>
            </>}>
      <p>
        È un autore del vecchio archivio: non compare più fra gli utenti e non gli si potrà dare l’accesso.
        Il suo nome resta sui percorsi, sugli interventi e sui servizi che ha registrato. Se serve, lo
        ritrovi con lo stato «Eliminati dall’elenco» e lo ripristini.
      </p>
      <Campo etichetta="Motivo (facoltativo)" valore={motivo} cambia={setMotivo} maxLength={500}
             aiuto="Almeno cinque caratteri, se lo scrivi. Resta nella storia dell’utenza." />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/** Ultimo passo dell'invito, anche per «Dai l'accesso» dall'albo dei mediatori. */
export function ConsegnaInvito({ utente, invito, chiudi, titolo, nota }: {
  utente: PersonaInvitata; invito: InvitoConsegnato; chiudi: () => void
  /** Titolo della finestra, quando la consegna chiude un percorso diverso dall'aggiunta (riattivazione). */
  titolo?: string
  nota?: string
}) {
  const [copiato, setCopiato] = useState<'si' | 'no' | null>(null)
  const scade = new Date(invito.scade_il).toLocaleString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  })

  async function copia() {
    try {
      await navigator.clipboard.writeText(invito.collegamento)
      setCopiato('si')
    } catch {
      setCopiato('no')
    }
  }

  return (
    <Modale titolo={titolo ?? 'Aggiungi una persona · consegna l’invito'} chiudi={chiudi} larga
            azioni={<Bottone variante="primario" onClick={chiudi}>Fatto</Bottone>}>
      {invito.email_inviata
        ? (
          <Avviso tipo="successo">
            L’email con il collegamento è partita verso {utente.nome} {utente.cognome}
            {invito.inviato_a === 'recupero' ? ', alla sua email personale' : ''}.
          </Avviso>
        )
        : (
          <Avviso tipo="attenzione">
            L’email non è partita{invito.motivo_email ? ` (${invito.motivo_email})` : ''}. Mandagli tu il
            collegamento qui sotto: per messaggio, WhatsApp o come preferisci.
          </Avviso>
        )}

      <h3 className="rete-titoletto">1. Il collegamento</h3>
      <p className="nota-modulo">
        {invito.email_inviata ? 'È lo stesso dell’email. Ti serve solo se l’email non arriva.' : 'Copialo e mandalo a chi stai invitando.'}
      </p>
      <div className="riga">
        <input className="operatori-collegamento" readOnly value={invito.collegamento}
               aria-label="Collegamento dell’invito" onFocus={(e) => e.currentTarget.select()} />
        <Bottone dimensione="piccolo" onClick={() => void copia()}>
          {copiato === 'si' ? 'Copiato' : 'Copia il collegamento'}
        </Bottone>
      </div>
      {copiato === 'no' && <p className="nota-modulo">Il browser non permette di copiare: seleziona il testo e copialo a mano.</p>}

      <h3 className="rete-titoletto">2. Il codice, a voce</h3>
      <p className="operatori-codice-invito" aria-label="Codice da dettare">{invito.codice_da_consegnare_a_voce}</p>
      <p className="nota-modulo">
        Dettalo a {utente.nome} di persona o per telefono, non per scritto insieme al collegamento:
        senza il codice, chi trovasse l’email non può entrare al suo posto.
      </p>

      <p className="nota-modulo">
        L’invito vale fino a {scade}. Se scade prima che {utente.nome} entri, dalla sua riga
        nell’elenco puoi mandarne uno nuovo.
      </p>
      {nota && <p className="nota-modulo">{nota}</p>}
    </Modale>
  )
}
